import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { fmtBRL, fmtPct } from "@/lib/format";

const TODAY = new Date().toISOString().slice(0, 10);

const empty = {
  salesperson_id: "",
  date: TODAY,
  revenue: "",
  cost: "",
  client: "",
  product: "",
  note: "",
};

export function SaleFormModal({ open, onOpenChange, sale, salespeople, onSubmit, submitting }) {
  const isEdit = Boolean(sale);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm(
        sale
          ? {
              salesperson_id: sale.salesperson_id,
              date: sale.date,
              revenue: String(sale.revenue),
              cost: String(sale.cost),
              client: sale.client || "",
              product: sale.product || "",
              note: sale.note || "",
            }
          : empty
      );
    }
  }, [open, sale]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const { lucro, margem } = useMemo(() => {
    const r = parseFloat(form.revenue) || 0;
    const c = parseFloat(form.cost) || 0;
    const l = r - c;
    return { lucro: l, margem: r > 0 ? (l / r) * 100 : 0 };
  }, [form.revenue, form.cost]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.salesperson_id) return setError("Selecione o vendedor.");
    const r = parseFloat(form.revenue);
    const c = parseFloat(form.cost);
    if (!(r > 0)) return setError("Receita deve ser maior que zero.");
    if (!(c >= 0)) return setError("Custo não pode ser negativo.");
    if (form.date > TODAY) return setError("Data futura não é permitida.");

    const payload = {
      salesperson_id: form.salesperson_id,
      date: form.date,
      revenue: r,
      cost: c,
      client: form.client || null,
      product: form.product || null,
      note: form.note || null,
    };
    try {
      await onSubmit(payload);
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível salvar a venda.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sale-form-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {isEdit ? "Editar venda" : "Nova venda"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div data-testid="sale-form-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {typeof error === "string" ? error : "Erro de validação."}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Vendedor *</Label>
              <Select value={form.salesperson_id} onValueChange={set("salesperson_id")}>
                <SelectTrigger data-testid="sale-salesperson-select">
                  <SelectValue placeholder="Selecione o vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {salespeople.map((sp) => (
                    <SelectItem key={sp.salesperson_id} value={sp.salesperson_id}>
                      {sp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="sale-date">Data *</Label>
              <Input id="sale-date" type="date" max={TODAY} value={form.date} onChange={set("date")} data-testid="sale-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sale-revenue">Receita (R$) *</Label>
              <Input id="sale-revenue" type="number" step="0.01" min="0" value={form.revenue} onChange={set("revenue")} data-testid="sale-revenue-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sale-cost">Custo (R$) *</Label>
              <Input id="sale-cost" type="number" step="0.01" min="0" value={form.cost} onChange={set("cost")} data-testid="sale-cost-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sale-client">Cliente</Label>
              <Input id="sale-client" value={form.client} onChange={set("client")} data-testid="sale-client-input" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="sale-product">Produto/Serviço</Label>
              <Input id="sale-product" value={form.product} onChange={set("product")} data-testid="sale-product-input" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="sale-note">Observação</Label>
              <Textarea id="sale-note" rows={2} value={form.note} onChange={set("note")} data-testid="sale-note-input" />
            </div>
          </div>

          {/* Cálculo automático */}
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-4 py-3">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Lucro</p>
              <p data-testid="sale-computed-lucro" className="font-num text-lg font-semibold">{fmtBRL(lucro)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Margem</p>
              <p data-testid="sale-computed-margem" className="font-num text-lg font-semibold">{fmtPct(margem)}</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" data-testid="sale-submit-button" disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90">
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEdit ? "Salvar alterações" : "Registrar venda"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
